import React, { useMemo, useState } from "react";
import styled from "styled-components";
import DataTable from "src/components/Table";
import { Cell } from "src/components/Table/Cell";
import TableRow from "src/components/Table/TableRow";
import Search from "src/components/molecules/WorkRequestListViewContainer/components/Search";
import { colors } from "src/styles";

interface RelatedResourceOption {
  id: string;
  name: string;
  details?: string;
}

interface RelatedResourceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceType: "vendor" | "team_member";
  resources: RelatedResourceOption[];
  selectedResourceId?: string;
  onConfirm: (resourceId: string) => void;
}

const RESOURCE_TABLE_HEADERS = ["Resource Id", "Name", "Details"];

export const RelatedResourceSelectionModal = ({
  isOpen,
  onClose,
  resourceType,
  resources,
  selectedResourceId,
  onConfirm,
}: RelatedResourceSelectionModalProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selected, setSelected] = useState(selectedResourceId || "");

  const effectiveSearch = (searchValue || appliedSearch).trim().toLowerCase();

  const displayedResources = useMemo(() => {
    if (!effectiveSearch) return resources;

    return resources.filter((resource) => {
      const id = String(resource.id || "").toLowerCase();
      const name = (resource.name || "").toLowerCase();
      const details = (resource.details || "").toLowerCase();
      return id.includes(effectiveSearch) || name.includes(effectiveSearch) || details.includes(effectiveSearch);
    });
  }, [resources, effectiveSearch]);

  const selectedResource = useMemo(() => resources.find((resource) => resource.id === selected), [resources, selected]);

  const closeAndReset = () => {
    setSearchValue("");
    setAppliedSearch("");
    setSelected(selectedResourceId || "");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay onClick={closeAndReset} />
      <Modal>
        <Header>
          <Title>Select {resourceType === "vendor" ? "Vendor" : "Team Member"}</Title>
          <CloseButton type="button" onClick={closeAndReset}>
            x
          </CloseButton>
        </Header>

        <Body>
          <SearchWrapper>
            <Search
              value={searchValue}
              applied={!!appliedSearch}
              onChange={setSearchValue}
              onClickApply={() => setAppliedSearch(searchValue.trim())}
              onClickCancel={() => setSearchValue(appliedSearch)}
              onClickClear={() => {
                setSearchValue("");
                setAppliedSearch("");
              }}
            />
          </SearchWrapper>

          <SelectedSummary>
            <SummaryLabel>Selected {resourceType === "vendor" ? "Vendor" : "Team Member"}</SummaryLabel>
            <SummaryValue>
              {selectedResource
                ? `${selectedResource.name} (${selectedResource.id})`
                : `No ${resourceType === "vendor" ? "vendor" : "team member"} selected`}
            </SummaryValue>
          </SelectedSummary>

          <TableWrapper>
            {displayedResources.length > 0 ? (
              <DataTable headers={RESOURCE_TABLE_HEADERS}>
                {displayedResources.map((resource) => (
                  <TableRow
                    key={resource.id}
                    $clickable={true}
                    onClick={() => setSelected(resource.id)}
                    $bgColor={selected === resource.id ? colors.grey.light1 : colors.white.default}
                  >
                    <Cell showMark highlight={effectiveSearch}>
                      {resource.id}
                    </Cell>
                    <Cell showMark highlight={effectiveSearch}>
                      {resource.name || "Unnamed"}
                    </Cell>
                    <Cell showMark highlight={effectiveSearch}>
                      {resource.details || "-"}
                    </Cell>
                  </TableRow>
                ))}
              </DataTable>
            ) : (
              <EmptyState>No {resourceType === "vendor" ? "vendors" : "team members"} match your search.</EmptyState>
            )}
          </TableWrapper>
        </Body>

        <Footer>
          <SecondaryButton type="button" onClick={closeAndReset}>
            Cancel
          </SecondaryButton>
          <SecondaryButton type="button" onClick={() => setSelected("")}>
            Clear Selection
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={!selected}
            onClick={() => {
              if (!selected) return;
              onConfirm(selected);
              closeAndReset();
            }}
          >
            Select {resourceType === "vendor" ? "Vendor" : "Team Member"}
          </PrimaryButton>
        </Footer>
      </Modal>
    </>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 999;
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(1200px, 96vw);
  max-height: 88vh;
  background: #ffffff;
  border: 1px solid #dde2e7;
  display: flex;
  flex-direction: column;
  z-index: 1000;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dde2e7;
  padding: 16px 20px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2a37;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  color: #6f7782;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 16px 20px;
  overflow: auto;
`;

const SearchWrapper = styled.div`
  margin-bottom: 12px;

  > div {
    width: 100%;
  }
`;

const SelectedSummary = styled.div`
  border: 1px solid #dde2e7;
  background: #f9fafb;
  padding: 10px 12px;
  margin-bottom: 12px;
`;

const SummaryLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6f7782;
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 13px;
  color: #1f2a37;
`;

const TableWrapper = styled.div`
  border: 1px solid #dde2e7;
`;

const EmptyState = styled.div`
  padding: 28px;
  text-align: center;
  color: #6f7782;
  font-size: 13px;
`;

const Footer = styled.div`
  border-top: 1px solid #dde2e7;
  padding: 12px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const SecondaryButton = styled.button`
  border: 1px solid #c9d2dc;
  background: #ffffff;
  color: #1f2a37;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
`;

const PrimaryButton = styled.button`
  border: 1px solid #1f2a37;
  background: #1f2a37;
  color: #ffffff;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    background: #c9d2dc;
    border-color: #c9d2dc;
    cursor: not-allowed;
  }
`;
